'use client';

import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { extractComponentFromChunk, extractProductInfoFromChunk } from '@/ui/utils/tiles';
import Container from '../../shared/Container';

import 'swiper/css';
import 'swiper/css/pagination';
import './styles.css';

import OptimizedImage from '../../shared/OptimizedImage';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import ButtonLink from '../../shared/ButtonLink';
import { ProductOneColumnSliderTile } from './types/ProductOneColumnSliderTile';

export default function ProductOneColumnSlider({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: ProductOneColumnSliderTile;
  trackingPrefix?: string;
}>) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const { title: currentTitle, shortDescription: currentShortDescription } =
    extractProductInfoFromChunk(tile.cards.chunks[currentIdx]);
  const currentCta = extractComponentFromChunk(
    tile.cards.chunks[currentIdx],
    'cta',
    'PieceContent',
  );

  return (
    <Container>
      <Swiper
        slidesPerView={1}
        spaceBetween={10}
        modules={[Pagination]}
        pagination={{
          clickable: true,
        }}
        className="product-one-col-slider h-xs w-full md:h-md xl:h-lg"
        onSlideChange={(swiper) => setCurrentIdx(swiper.activeIndex)}
      >
        {tile.cards.chunks.map((card, i) => {
          const image = extractComponentFromChunk(card, 'image', 'ImageContent');
          const { title, shortDescription } = extractProductInfoFromChunk(card);
          const cta = extractComponentFromChunk(card, 'cta', 'PieceContent');

          return (
            <SwiperSlide
              key={`${i}one-col-slider`}
              className="group relative z-0 overflow-hidden rounded-xl"
            >
              <OptimizedImage
                imageComponent={image}
                fill
                className="-z-20 object-cover"
                sizes="100vw"
              />
              <div className=" absolute inset-0 -z-10 bg-gradient-middle" />
              <div className=" flex h-full w-full items-center justify-center text-white">
                <div className="hidden space-y-2 text-center sm:block">
                  <h3 className="font-bold sm:text-3xl md:text-4xl lg:text-5xl">{title}</h3>
                  <RichTextRenderer blocks={shortDescription} className="md:text-xl lg:text-2xl" />
                  {cta && (
                    <ButtonLink
                      ctaComponent={cta}
                      className="mx-auto mt-4"
                      trackingPrefix={trackingPrefix}
                    />
                  )}
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
      <div className="text-center sm:hidden">
        <h3 className="text-3xl font-bold">{currentTitle}</h3>
        <RichTextRenderer blocks={currentShortDescription} className="text-md" />
        {currentCta && (
          <ButtonLink
            ctaComponent={currentCta}
            className="mx-auto mt-4"
            trackingPrefix={trackingPrefix}
          />
        )}
      </div>
    </Container>
  );
}
